import type { FC, FormEvent, ReactNode } from 'react'
import type { BuildSummary, ForkliftModel } from '@/types'

import { useEffect, useId, useState } from 'react'
import { ArrowRight, CircleAlert, CircleCheck, LoaderCircle, Pencil, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cmsTag } from '@/lib/cms'
import { useStore } from '@/lib/store'
import { quoteStore } from '@/lib/configurator'
import { formatUsd } from '@/lib/format'
import { Reveal } from '@/components/generals/reveal'
import { SectionHeading } from '@/components/landing/section-heading'

import {
    purchaseTypes,
    quotePerks,
    timelines
} from '@/constants/content'

type Status = 'idle' | 'sending' | 'sent' | 'error'

type Fields = {
    name: string
    email: string
    company: string
    phone: string
    model: string
    quantity: string
    timeline: string
    purchaseType: string
    message: string
    website: string
}

const EMAIL_PATTERN = /^[^\s@<>()]+@[^\s@<>()]+\.[^\s@<>()]{2,}$/

const inputClass = (invalid: boolean) => cn(
    'mt-2 w-full rounded-xl border bg-white/[0.03] px-4 py-3 text-[15px] text-white placeholder:text-white/30 transition-colors focus:border-brand focus:bg-white/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
    invalid ? 'border-destructive' : 'border-white/12 hover:border-white/25'
)

const selectClass = cn(
    inputClass(false),
    'appearance-none bg-[url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20viewBox%3D%270%200%2024%2024%27%20fill%3D%27none%27%20stroke%3D%27%23ffffff99%27%20stroke-width%3D%272%27%3E%3Cpath%20d%3D%27m6%209%206%206%206-6%27/%3E%3C/svg%3E")] bg-[length:18px] bg-[right_14px_center] bg-no-repeat pr-11'
)

interface FieldProps {
    id: string
    label: string
    error?: string
    optional?: boolean
    children: ReactNode
}

const Field: FC<FieldProps> = ({ id, label, error, optional, children }) => {
    return (
        <div>
            <label htmlFor={id} className='flex items-center justify-between text-sm font-medium text-white/85'>
                {label}
                {optional && <span className='text-xs font-normal text-white/40'>Optional</span>}
            </label>
            {children}
            {error && (
                <p id={`${id}-error`} className='mt-1.5 flex items-center gap-1.5 text-xs text-destructive'>
                    <CircleAlert aria-hidden='true' className='size-3.5' />
                    {error}
                </p>
            )}
        </div>
    )
}

interface QuoteFormProps {
    models: ForkliftModel[]
}

export const QuoteForm: FC<QuoteFormProps> = ({ models }) => {
    const id = useId()
    const { build, version } = useStore(quoteStore, state => state)
    const [attached, setAttached] = useState<BuildSummary | null>(null)
    const [status, setStatus] = useState<Status>('idle')
    const [reference, setReference] = useState('')
    const [serverMessage, setServerMessage] = useState('')
    const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({})
    const [fields, setFields] = useState<Fields>({
        name: '',
        email: '',
        company: '',
        phone: '',
        model: '',
        quantity: '1',
        timeline: '',
        purchaseType: '',
        message: '',
        website: ''
    })

    useEffect(() => {
        if (!build || !version) return

        setAttached(build)
        setFields(current => ({ ...current, model: build.modelSlug }))
        setStatus(current => (current === 'sent' ? 'idle' : current))
    }, [build, version])

    const set = (key: keyof Fields) => (value: string) => {
        setFields(current => ({ ...current, [key]: value }))

        if (errors[key]) setErrors(current => ({ ...current, [key]: undefined }))
        if (key === 'model' && attached && value !== attached.modelSlug) setAttached(null)
    }

    const validate = () => {
        const next: Partial<Record<keyof Fields, string>> = {}
        const quantity = Number(fields.quantity)

        if (fields.name.trim().length < 2) next.name = "Please enter your full name."
        if (!EMAIL_PATTERN.test(fields.email.trim())) next.email = "Please enter a valid work email."
        if (!fields.company.trim()) next.company = "Please enter your company name."
        if (fields.phone.trim() && !/^[0-9+()\-.\s]{6,40}$/.test(fields.phone.trim())) next.phone = "Please enter a valid phone number."
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > 500) next.quantity = "Choose between 1 and 500 trucks."

        setErrors(next)

        return next
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'sending') return

        const problems = validate()
        const firstProblem = Object.keys(problems)[0]

        if (firstProblem) {
            document.getElementById(`${id}-${firstProblem}`)?.focus()

            return
        }

        setStatus('sending')
        setServerMessage('')

        try {
            const response = await fetch('/api/quote', {
                method: "POST",
                headers: { "Content-Type": 'application/json' },
                body: JSON.stringify({
                    ...fields,
                    quantity: Number(fields.quantity),
                    configuration: attached && attached.modelSlug === fields.model ? attached.configuration : null
                })
            })

            const result = await response.json().catch(() => null) as { ok?: boolean, reference?: string, message?: string, fields?: Partial<Record<keyof Fields, string>> } | null

            if (!response.ok || !result?.ok) {
                if (result?.fields) setErrors(result.fields)

                setServerMessage(result?.message ?? "Something went wrong. Please try again.")
                setStatus('error')

                return
            }

            setReference(result.reference ?? '')
            setStatus('sent')
        }
        catch {
            setServerMessage("We could not reach our server. Check your connection and try again.")
            setStatus('error')
        }
    }

    const reset = () => {
        setFields(current => ({ ...current, message: '', website: '' }))
        setStatus('idle')
        setReference('')
    }

    const describedBy = (key: keyof Fields) => (errors[key] ? `${id}-${key}-error` : undefined)
    const attachedModel = attached ? models.find(model => model.slug === attached.modelSlug) : undefined

    return (
        <section id='quote' aria-labelledby='quote-title' className='relative z-10 scroll-mt-16 border-t border-white/[0.07] bg-surface py-24 sm:py-32'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-[480px] bg-[radial-gradient(60%_100%_at_50%_0%,oklch(0.86_0.17_86/0.08),transparent_70%)]' />

            <div className='relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.3fr] lg:gap-20'>
                <div>
                    <SectionHeading
                        id='quote-title'
                        eyebrow="Get a quote"
                        title="Tell us what you move."
                        body="Share a few details and a FORQ specialist will send a tailored quote, with lease and finance options, within one business day. No obligation."
                    />

                    <Reveal delay={120}>
                        <ul className='mt-10 space-y-4'>
                            {quotePerks.map(perk => (
                                <li key={perk} className='flex items-center gap-3 text-sm text-white/80'>
                                    <CircleCheck aria-hidden='true' className='size-5 shrink-0 text-brand' />
                                    {perk}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>

                <Reveal delay={60}>
                    <div className='rounded-3xl border border-white/10 bg-card p-6 shadow-[0_40px_120px_-50px_rgba(0,0,0,0.9)] sm:p-9'>
                        {status === 'sent' ? (
                            <div role='status' className='flex min-h-[420px] flex-col items-start justify-center'>
                                <span className='flex size-14 items-center justify-center rounded-2xl bg-brand text-brand-foreground'>
                                    <CircleCheck className='size-7' />
                                </span>
                                <h3 className='mt-7 font-heading font-expanded text-2xl font-extrabold text-white sm:text-3xl'>
                                    Your request is in.
                                </h3>
                                <p className='mt-4 max-w-md text-base leading-relaxed text-white/70'>
                                    Thanks, {fields.name.split(' ')[0]}. A FORQ specialist will reply to {fields.email} within one business day with your quote.
                                </p>
                                {reference && (
                                    <p className='mt-6 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-sm text-white/80'>
                                        Reference <span className='font-semibold text-brand'>{reference}</span>
                                    </p>
                                )}
                                <button
                                    type='button'
                                    onClick={reset}
                                    className='mt-8 text-sm font-semibold text-white underline decoration-white/30 underline-offset-4 transition-colors hover:text-brand hover:decoration-brand'
                                >
                                    Send another request
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} noValidate className='grid gap-5'>
                                {attached && attached.modelSlug === fields.model && (
                                    <div className='flex items-start justify-between gap-4 rounded-2xl border border-brand/30 bg-brand/[0.06] p-4'>
                                        <div className='min-w-0'>
                                            <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-brand'>
                                                Your build
                                            </p>
                                            <p className='mt-1.5 text-sm font-semibold text-white'>
                                                {attachedModel ? (
                                                    <>
                                                        <span data-mdlfy-cms={cmsTag('forklift_models', attachedModel.id, 'name')}>{attachedModel.name}</span>
                                                        {' '}
                                                        <span data-mdlfy-cms={cmsTag('forklift_models', attachedModel.id, 'edition')}>{attachedModel.edition}</span>
                                                    </>
                                                ) : attached.modelName}
                                                <span className='font-normal text-white/60'> · {formatUsd(attached.totalUsd)} estimated</span>
                                            </p>
                                            <p className='mt-1 text-xs leading-relaxed text-white/60'>
                                                {attached.lines.join(', ')}
                                            </p>
                                        </div>
                                        <div className='flex shrink-0 gap-1'>
                                            <a
                                                href='#configure'
                                                aria-label="Edit your build"
                                                className='inline-flex size-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white'
                                            >
                                                <Pencil className='size-4' />
                                            </a>
                                            <button
                                                type='button'
                                                onClick={() => setAttached(null)}
                                                aria-label="Remove your build from this request"
                                                className='inline-flex size-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white'
                                            >
                                                <X className='size-4' />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className='grid gap-5 sm:grid-cols-2'>
                                    <Field id={`${id}-name`} label="Full name" error={errors.name}>
                                        <input
                                            id={`${id}-name`}
                                            name='name'
                                            autoComplete='name'
                                            value={fields.name}
                                            onChange={event => set('name')(event.target.value)}
                                            aria-invalid={Boolean(errors.name)}
                                            aria-describedby={describedBy('name')}
                                            className={inputClass(Boolean(errors.name))}
                                            placeholder="Jordan Lee"
                                        />
                                    </Field>

                                    <Field id={`${id}-company`} label="Company" error={errors.company}>
                                        <input
                                            id={`${id}-company`}
                                            name='company'
                                            autoComplete='organization'
                                            value={fields.company}
                                            onChange={event => set('company')(event.target.value)}
                                            aria-invalid={Boolean(errors.company)}
                                            aria-describedby={describedBy('company')}
                                            className={inputClass(Boolean(errors.company))}
                                            placeholder="Lee Logistics"
                                        />
                                    </Field>

                                    <Field id={`${id}-email`} label="Work email" error={errors.email}>
                                        <input
                                            id={`${id}-email`}
                                            name='email'
                                            type='email'
                                            autoComplete='email'
                                            value={fields.email}
                                            onChange={event => set('email')(event.target.value)}
                                            aria-invalid={Boolean(errors.email)}
                                            aria-describedby={describedBy('email')}
                                            className={inputClass(Boolean(errors.email))}
                                            placeholder="jordan@company.com"
                                        />
                                    </Field>

                                    <Field id={`${id}-phone`} label="Phone" error={errors.phone} optional>
                                        <input
                                            id={`${id}-phone`}
                                            name='phone'
                                            type='tel'
                                            autoComplete='tel'
                                            value={fields.phone}
                                            onChange={event => set('phone')(event.target.value)}
                                            aria-invalid={Boolean(errors.phone)}
                                            aria-describedby={describedBy('phone')}
                                            className={inputClass(Boolean(errors.phone))}
                                            placeholder="(555) 014-2290"
                                        />
                                    </Field>

                                    <Field id={`${id}-model`} label="Model of interest">
                                        <select
                                            id={`${id}-model`}
                                            name='model'
                                            value={fields.model}
                                            onChange={event => set('model')(event.target.value)}
                                            className={selectClass}
                                        >
                                            <option value=''>Not sure yet</option>
                                            {models.map(model => (
                                                <option key={model.slug} value={model.slug}>
                                                    {model.name} {model.edition}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>

                                    <Field id={`${id}-quantity`} label="Number of trucks" error={errors.quantity}>
                                        <input
                                            id={`${id}-quantity`}
                                            name='quantity'
                                            type='number'
                                            inputMode='numeric'
                                            min={1}
                                            max={500}
                                            value={fields.quantity}
                                            onChange={event => set('quantity')(event.target.value)}
                                            aria-invalid={Boolean(errors.quantity)}
                                            aria-describedby={describedBy('quantity')}
                                            className={inputClass(Boolean(errors.quantity))}
                                        />
                                    </Field>

                                    <Field id={`${id}-timeline`} label="When do you need them?" optional>
                                        <select
                                            id={`${id}-timeline`}
                                            name='timeline'
                                            value={fields.timeline}
                                            onChange={event => set('timeline')(event.target.value)}
                                            className={selectClass}
                                        >
                                            <option value=''>Choose a timeline</option>
                                            {timelines.map(item => (
                                                <option key={item} value={item}>{item}</option>
                                            ))}
                                        </select>
                                    </Field>

                                    <fieldset>
                                        <legend className='flex w-full items-center justify-between text-sm font-medium text-white/85'>
                                            How would you like to pay?
                                            <span className='text-xs font-normal text-white/40'>Optional</span>
                                        </legend>
                                        <div className='mt-2 grid grid-cols-2 gap-2'>
                                            {purchaseTypes.map(item => (
                                                <label
                                                    key={item}
                                                    className={cn(
                                                        'cursor-pointer rounded-xl border px-3 py-2.5 text-center text-sm transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-brand',
                                                        fields.purchaseType === item ? 'border-brand bg-brand/[0.08] text-white' : 'border-white/12 text-white/70 hover:border-white/25'
                                                    )}
                                                >
                                                    <input
                                                        type='radio'
                                                        name='purchaseType'
                                                        value={item}
                                                        checked={fields.purchaseType === item}
                                                        onChange={() => set('purchaseType')(item)}
                                                        className='sr-only'
                                                    />
                                                    {item}
                                                </label>
                                            ))}
                                        </div>
                                    </fieldset>
                                </div>

                                <Field id={`${id}-message`} label="Tell us about your site" optional>
                                    <textarea
                                        id={`${id}-message`}
                                        name='message'
                                        rows={4}
                                        maxLength={2000}
                                        value={fields.message}
                                        onChange={event => set('message')(event.target.value)}
                                        className={cn(inputClass(false), 'resize-y')}
                                        placeholder="Loads, aisle widths, indoor or outdoor work, shifts per day..."
                                    />
                                </Field>

                                <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                                    <label htmlFor={`${id}-website`}>Website</label>
                                    <input
                                        id={`${id}-website`}
                                        name='website'
                                        tabIndex={-1}
                                        autoComplete='off'
                                        value={fields.website}
                                        onChange={event => set('website')(event.target.value)}
                                    />
                                </div>

                                {status === 'error' && serverMessage && (
                                    <p role='alert' className='flex items-center gap-2 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-white'>
                                        <CircleAlert aria-hidden='true' className='size-4 shrink-0 text-destructive' />
                                        {serverMessage}
                                    </p>
                                )}

                                <div className='flex flex-col-reverse items-start gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between'>
                                    <p className='text-xs leading-relaxed text-white/45 sm:max-w-xs'>
                                        We only use your details to prepare your quote.
                                    </p>
                                    <button
                                        type='submit'
                                        disabled={status === 'sending'}
                                        className='group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-brand-foreground transition-[transform,box-shadow,opacity] hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-12px_var(--brand)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:translate-y-0 disabled:opacity-70 sm:w-auto sm:text-base'
                                    >
                                        {status === 'sending' ? (
                                            <>
                                                <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                                                Sending
                                            </>
                                        ) : (
                                            <>
                                                Request my quote
                                                <ArrowRight aria-hidden='true' className='size-4 transition-transform group-hover:translate-x-0.5' />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
